import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { api, HOW_LABEL, type Card, type UnansweredQuestion } from '../lib/api';
import { gapGroups, questionCounts, waitingQuestions } from '../lib/cards';
import { answersOpen, answersSeen, markAnswersSeen } from '../lib/layout';

const session = () => { try { return window.sessionStorage; } catch { return null; } };

/**
 * "Answer these first" (fix-40), above the draft only when the drafter left questions out (fix-73). The drafter never
 * promises to get back to a customer: what it could not answer is left out of the reply and listed here. Each question
 * has its own box and "Add to draft", which writes the answer into the reply (one Refine with it as a fact) — your
 * unsaved edits are saved first — and then offers the answer as an operator article, so the next draft reads it as
 * knowledge instead of asking again.
 *
 * partly-1 (PSA #312): each gap under the question of theirs it is part of, in their order, Other last; a card whose
 * gaps name no question lists them as before.
 *
 * fix-74: shown in full the first time; once the questions have been seen (the draft focused, or the card left) or all
 * answered, it is one line on the next visit, so the draft starts near the top. A click on the line opens it.
 */
export function AnswersPanel({ card, pending, busy, onAnswer, run, draftFocused }: {
  card: Card;
  pending: boolean;
  busy: boolean;
  onAnswer: (i: number, fact: string) => Promise<boolean>;
  run: (label: string, fn: () => Promise<unknown>) => Promise<void>;
  draftFocused: boolean;
}) {
  const questions = card.payload.unanswered_questions ?? [];
  const [facts, setFacts] = useState<string[]>(() => questions.map(() => ''));
  useEffect(() => { setFacts(questions.map(() => '')); }, [card.id, questions.length]);
  // Seen is read once, when the card opens, and marked when it is left or the draft is focused: it decides the next
  // visit, so nothing above the editor moves while he is in it.
  const [view, setView] = useState(() => ({ id: card.id, seen: answersSeen(session(), card.id), toggled: null as boolean | null }));
  if (view.id !== card.id) setView({ id: card.id, seen: answersSeen(session(), card.id), toggled: null });
  const { seen, toggled } = view;
  const setToggled = (t: boolean) => setView((v) => ({ ...v, toggled: t }));
  useEffect(() => () => { if (questions.length) markAnswersSeen(session(), card.id); }, [card.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (draftFocused && questions.length) markAnswersSeen(session(), card.id); }, [draftFocused, card.id, questions.length]);

  if (!questions.length) return null;
  const waiting = waitingQuestions(card).length;
  const groups = gapGroups(card);
  const of = groups?.filter((g) => g.n != null && g.gaps.some(({ q }) => !q.answer?.trim())).length ?? 0;
  const count = !waiting ? 'all answered' : groups ? `${waiting} to answer${of ? `, in ${of} of their questions` : ''}`
    : `${waiting} question${waiting === 1 ? '' : 's'}`;

  if (!answersOpen({ waiting, seen, toggled })) {
    const first = waitingQuestions(card)[0]?.question;
    return (
      <button type="button" className="answers answers-line" onClick={() => setToggled(true)} aria-expanded="false">
        <strong>Answer these first · {count}</strong>
        {first && <span className="answers-line-q">{first}</span>}
        <span className="mono answers-line-show">show</span>
      </button>
    );
  }

  const add = async (i: number) => {
    const fact = (facts[i] ?? '').trim();
    if (fact && await onAnswer(i, fact)) setFacts((f) => f.map((x, k) => (k === i ? '' : x)));
  };

  const saveArticle = (i: number) => run(`article-${i}`, async () => {
    const q = questions[i];
    const r = await api.knowledge.create({
      title: q.question.slice(0, 200), body_md: q.answer ?? '', audience: 'internal', weight: 150,
      supersedes_id: null, reason: `answered on card #${card.id}`,
    });
    await api.answers(card.id, { saved: { i, article_id: r.article.id } });
  });

  const item = (q: UnansweredQuestion, i: number) => (
    <li key={i}>
      <div>
        <p className="answers-q">{q.question}</p>
        {(q.needed || q.searched.length > 0) && (
          <p className="answers-meta mono">
            {q.needed && <>needs: {q.needed}</>}
            {q.needed && q.searched.length > 0 && ' · '}
            {q.searched.length > 0 && <>searched: {q.searched.join('; ')}</>}
          </p>
        )}
      </div>
      {q.answer ? (
        <div className="answers-given">
          <span>{q.answer}</span>
          {q.article_id ? (
            <Link className="mono" href={`/knowledge/${q.article_id}`}>article #{q.article_id}</Link>
          ) : (
            <button type="button" className="btn-ghost btn-small" onClick={() => saveArticle(i)} disabled={busy}>
              Save as article
            </button>
          )}
        </div>
      ) : (
        <form className="answers-row" onSubmit={(e) => { e.preventDefault(); void add(i); }}>
          <label className="sr-only" htmlFor={`answer-${i}`}>Answer to: {q.question}</label>
          <input id={`answer-${i}`} className="answers-box" value={facts[i] ?? ''} disabled={!pending || busy}
            onChange={(e) => setFacts((f) => f.map((x, k) => (k === i ? e.target.value : x)))}
            placeholder="Type the fact…" />
          <button type="submit" className="btn-secondary" disabled={!pending || busy || !(facts[i] ?? '').trim()}>
            Add to draft
          </button>
        </form>
      )}
    </li>
  );

  return (
    <section className="answers">
      <div className="answers-top">
        <h3>Answer these first · {count}</h3>
        <span className="answers-note">your answer goes into the draft and is offered as an article</span>
        <button type="button" className="btn-ghost btn-small" onClick={() => setToggled(false)} aria-expanded="true">fold</button>
      </div>
      {groups ? groups.map((g) => (
        <div key={g.n ?? 'other'} className="answers-group">
          <p className="answers-group-q">{g.n ? <>{g.n}. {g.question}{g.how && <span className={`mono how how-${g.how}`}>
            {HOW_LABEL[g.how]}</span>}</> : 'Other'}</p>
          <ol className="answers-list">{g.gaps.map(({ q, i }) => item(q, i))}</ol>
        </div>
      )) : <ol className="answers-list">{questions.map((q, i) => item(q, i))}</ol>}
    </section>
  );
}

/** answer-1 (Royce, 2026-10-21): an email with two or more questions shows each, in their words, with how the draft
 * answered it, so what was answered is seen at a glance. Only those left to you are in "Answer these first". */
export function QuestionsList({ card }: { card: Card }) {
  const qs = card.payload.questions ?? [];
  if (qs.length < 2) return null;
  const gaps = card.payload.unanswered_questions ?? [];
  const left = (i: number) => gaps.filter((g) => g.of === i && !g.answer?.trim()).length; // partly-1
  return (
    <section className="questions">
      <h3>Their questions · {questionCounts(qs)}</h3>
      <ol className="answers-list">
        {qs.map((q, i) => (
          <li key={i}><p className="answers-q">{q.question} <span className={`mono how how-${q.how ?? 'none'}`}>
            {q.how ? HOW_LABEL[q.how] : 'not marked'}{left(i) ? ` · ${left(i)} to answer` : ''}</span></p></li>
        ))}
      </ol>
    </section>
  );
}
